"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const partnerBenefits = [
{
  icon: "EyeIcon",
  title: "Instant client context",
  desc: "See each pet's breed, age, allergies, and history the moment they walk in — no intake forms needed."
},
{
  icon: "BellIcon",
  title: "Automated client reminders",
  desc: "Send vaccine due alerts, appointment reminders, and training follow-ups automatically."
},
{
  icon: "GiftIcon",
  title: "Loyalty rewards & discounts",
  desc: "Offer PetFob-native rewards to keep clients returning and strengthen your brand relationship."
},
{
  icon: "ChartBarIcon",
  title: "Client retention insights",
  desc: "Track visit frequency, service uptake, and pet health trends to grow your practice intelligently."
}];


const partnerTypes = [
{ label: "Veterinary Clinics", emoji: "🏥" },
{ label: "DogFob Trainers", emoji: "🎓" },
{ label: "Groomers", emoji: "✂️" },
{ label: "Pet Stores", emoji: "🛒" },
{ label: "DogFob Walkers", emoji: "🦮" },
{ label: "Boarding Facilities", emoji: "🏠" }];


export default function BusinessBenefitsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".scroll-fade-up, .scroll-fade-left, .scroll-fade-right").forEach((el, i) => {
              setTimeout(() => el.classList.add("is-visible"), i * 90);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 relative overflow-hidden noise-overlay"
      style={{ background: "linear-gradient(145deg, #0F2027 0%, #1E3A3A 50%, #0F2027 100%)" }}>
      
      {/* Teal glow blobs */}
      <div
        className="absolute top-[-20%] right-[-10%] w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(14,165,160,0.12) 0%, transparent 70%)" }} />
      
      <div
        className="absolute bottom-[-15%] left-[-8%] w-[400px] h-[400px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(92,207,203,0.08) 0%, transparent 70%)" }} />
      

      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* Left: Copy */}
          <div className="flex flex-col gap-7">
            <div className="scroll-fade-left">
              <span
                className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4"
                style={{ background: "rgba(14,165,160,0.15)", color: "#5CCFCB" }}>
                
                <Icon name="BuildingOfficeIcon" size={12} />
                For Pet Businesses
              </span>
              <h2 className="text-4xl sm:text-[42px] font-extrabold text-white tracking-tight leading-tight mb-4">
                Know every client's pet{" "}
                <span className="gradient-text">before they arrive.</span>
              </h2>
              <p className="text-[#8AA4A4] leading-relaxed text-lg">
                DogFob gives your practice a live view of every client's pet profile — with their permission. No more paper intake forms. No more missed follow-ups.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {partnerBenefits.map((b, idx) =>
              <div
                key={b.title}
                className={`scroll-fade-up stagger-${idx + 1} glass-card-dark rounded-2xl p-5 hover:border-teal-500/30 transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 hover:shadow-xl`}>
                
                  <div className="w-9 h-9 rounded-xl bg-teal-500/15 flex items-center justify-center mb-3">
                    <Icon name={b.icon as "EyeIcon"} size={18} className="text-teal-400" />
                  </div>
                  <h3 className="text-sm font-bold text-white mb-1.5 font-heading">{b.title}</h3>
                  <p className="text-xs text-[#8AA4A4] leading-relaxed">{b.desc}</p>
                </div>
              )}
            </div>

            {/* Partner types */}
            <div className="scroll-fade-up flex flex-wrap gap-2 mt-2">
              {partnerTypes.map((pt) =>
              <span
                key={pt.label}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full text-[#8AA4A4] border border-white/10 hover:border-teal-500/40 hover:text-teal-400 transition-all duration-200">
                
                  <span>{pt.emoji}</span> {pt.label}
                </span>
              )}
            </div>

            <div className="scroll-fade-up">
              <Link href="/for-businesses" className="btn-primary py-3.5 px-7 text-base transition-all duration-200 hover:scale-105 hover:shadow-lg">
                <Icon name="ArrowRightIcon" size={18} />
                Apply as a Partner
              </Link>
            </div>
          </div>

          {/* Right: Image */}
          <div className="relative scroll-fade-right">
            <div className="rounded-3xl overflow-hidden" style={{ boxShadow: "0 24px 64px rgba(0,0,0,0.4)" }}>
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_14bded7e0-1768592579190.png"
                alt="Veterinarian in white coat examining a small dogfob on an examination table at a clinic"
                width={600}
                height={480}
                className="w-full h-[420px] object-cover" />
              
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F2027]/60 to-transparent" />
            </div>

            {/* Floating partner card */}
            <div
              className="absolute -bottom-5 -left-5 sm:-left-8 glass-card-dark rounded-2xl p-4 animate-float-b border border-white/10"
              style={{ boxShadow: "0 16px 48px rgba(0,0,0,0.3)" }}>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
                  <Icon name="StarIcon" size={18} className="text-teal-400" variant="solid" />
                </div>
                <div>
                  <p className="text-xs text-[#8AA4A4]">Avg. partner rating</p>
                  <p className="text-xl font-extrabold text-white font-heading">4.9 / 5.0</p>
                  <p className="text-[10px] text-teal-400">from 340+ partner reviews</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}